import api from './api';

export interface IncomeHead {
  id: string;  // CUID
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface OtherIncome {
  id: string;  // CUID
  projectId: number;
  project: {
    id: number;
    name: string;
  };
  incomeHeadId: string;  // CUID
  incomeHead: {
    id: string;  // CUID
    name: string;
    description?: string;
  };
  incomeDate: string;
  narration?: string;
  amount: number;
  status: 'approval_pending' | 'approved' | 'rejected';
  approvedBy?: number;
  approvedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface OtherIncomeFilters {
  projectId?: number;
  incomeHeadId?: string;  // CUID
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateOtherIncomeEntry {
  projectId: number;
  incomeHeadId: string;  // CUID
  incomeDate: string;
  narration?: string;
  amount: number;
}

export interface CreateOtherIncomesRequest {
  incomes: CreateOtherIncomeEntry[];
}

class OtherIncomeService {
  // Income Head methods
  async getIncomeHeads(): Promise<IncomeHead[]> {
    const response = await api.get('/other-income/income-heads');
    return response.data.data;
  }

  async createIncomeHead(data: { name: string; description?: string }): Promise<IncomeHead> {
    const response = await api.post('/other-income/income-heads', data);
    return response.data.data;
  }

  // Other Income methods
  async getOtherIncomes(filters: OtherIncomeFilters = {}): Promise<{
    data: OtherIncome[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalRecords: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  }> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/other-income?${params.toString()}`);
    return response.data;
  }

  async getOtherIncomeById(id: string): Promise<OtherIncome> {
    const response = await api.get(`/other-income/${id}`);
    return response.data.data;
  }

  async createOtherIncomes(data: CreateOtherIncomesRequest): Promise<{ count: number }> {
    const response = await api.post('/other-income', data);
    return response.data.data;
  }

  async updateOtherIncome(id: string, data: Partial<CreateOtherIncomeEntry>): Promise<OtherIncome> {
    const response = await api.put(`/other-income/${id}`, data);
    return response.data.data;
  }

  async deleteOtherIncome(id: string): Promise<void> {
    await api.delete(`/other-income/${id}`);
  }

  async approveOtherIncome(id: string): Promise<OtherIncome> {
    const response = await api.put(`/other-income/${id}/approve`);
    return response.data.data;
  }

  async rejectOtherIncome(id: string, rejectionReason: string): Promise<OtherIncome> {
    const response = await api.put(`/other-income/${id}/reject`, { rejectionReason });
    return response.data.data;
  }

  async bulkApproveOtherIncomes(ids: string[]): Promise<{ count: number }> {
    const response = await api.put('/other-income/bulk/approve', { ids });
    return response.data.data;
  }

  // Utility methods
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  getStatusDisplay(status: string): { text: string; className: string } {
    switch (status) {
      case 'approval_pending':
        return { text: 'Pending', className: 'status-pending' };
      case 'approved':
        return { text: 'Approved', className: 'status-approved' };
      case 'rejected':
        return { text: 'Rejected', className: 'status-rejected' };
      default:
        return { text: status, className: 'status-unknown' };
    }
  }

  getStatusOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'approval_pending', label: 'Pending Approval' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' }
    ];
  }

  validateIncomeEntry(entry: CreateOtherIncomeEntry): string | null {
    if (!entry.projectId) {
      return 'Project is required';
    }
    if (!entry.incomeHeadId) {
      return 'Income head is required';
    }
    if (!entry.incomeDate) {
      return 'Income date is required';
    }
    if (!entry.amount || entry.amount <= 0) {
      return 'Valid amount is required';
    }
    return null;
  }

  validateIncomeHead(data: { name: string; description?: string }): string | null {
    if (!data.name || data.name.trim().length === 0) {
      return 'Income head name is required';
    }
    if (data.name.trim().length < 2) {
      return 'Income head name must be at least 2 characters long';
    }
    if (data.name.trim().length > 100) {
      return 'Income head name must be less than 100 characters';
    }
    return null;
  }

  getTotalAmount(incomes: CreateOtherIncomeEntry[]): number {
    return incomes.reduce((total, income) => total + (income.amount || 0), 0);
  }

  formatAmountInWords(amount: number): string {
    // This is a simple implementation. You might want to use a proper number-to-words library
    if (amount === 0) return 'Zero';
    
    const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    if (amount < 10) return units[amount];
    if (amount < 20) return teens[amount - 10];
    if (amount < 100) return tens[Math.floor(amount / 10)] + (amount % 10 ? ' ' + units[amount % 10] : '');
    if (amount < 1000) return units[Math.floor(amount / 100)] + ' Hundred' + (amount % 100 ? ' ' + this.formatAmountInWords(amount % 100) : '');
    if (amount < 100000) return this.formatAmountInWords(Math.floor(amount / 1000)) + ' Thousand' + (amount % 1000 ? ' ' + this.formatAmountInWords(amount % 1000) : '');
    if (amount < 10000000) return this.formatAmountInWords(Math.floor(amount / 100000)) + ' Lakh' + (amount % 100000 ? ' ' + this.formatAmountInWords(amount % 100000) : '');
    
    return this.formatAmountInWords(Math.floor(amount / 10000000)) + ' Crore' + (amount % 10000000 ? ' ' + this.formatAmountInWords(amount % 10000000) : '');
  }

  exportToCSV(incomes: OtherIncome[]): void {
    const headers = [
      'ID',
      'Project',
      'Income Head',
      'Date',
      'Narration',
      'Amount',
      'Status',
      'Created Date'
    ];

    const csvContent = [
      headers.join(','),
      ...incomes.map(income => [
        income.id,
        `"${income.project.name}"`,
        `"${income.incomeHead.name}"`,
        this.formatDate(income.incomeDate),
        `"${income.narration || ''}"`,
        income.amount,
        income.status,
        this.formatDate(income.createdAt)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `other-income-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Upload other income data from Excel
  async uploadOtherIncomeData(file: File, projectId: number): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId.toString());

    const response = await api.post('/upload-data/other-income', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'x-hidden-mode': 'true'
      }
    });
    
    return response.data;
  }
}

export const otherIncomeService = new OtherIncomeService();
